// @ts-strict-ignore
import { Directive, Input, NgZone, ViewChild } from '@angular/core';
import { SohoHomePageComponent } from 'ids-enterprise-ng';
import { Observable, Observer, of } from 'rxjs';
import { LMThemeService } from '@banner/lm-theme.service';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMDialogInfo, LMRefreshRequest } from '@base/lm-base.component';
import { LMNavigationService } from '@components/navigation/lm-navigation.service';
import { AppConstants } from '@app/utils';
import { LMErrorUtilService, LMOverlayService, LMUrlUtilService } from '@app/services';

@Directive()
export abstract class LMDashboardPanelBaseComponent {

  /**
   * The number of columns the card should layout into (given available space).
   */
  @Input() columns: number;
  @Input() set fromDataView(dataView: LMDataView) {}
  @Input() panel: LandmarkPanel;
  @Input() isFlyoutContent = false;

  @ViewChild(SohoHomePageComponent) sohoHomepageComponent: SohoHomePageComponent;

  constructor(
    protected lmThemeService: LMThemeService,
    protected lmOverlayService: LMOverlayService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmNavigationService: LMNavigationService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected zone: NgZone
  ) {}

  isModified(): boolean {
    return false;
  }

  public refresh(request?: LMRefreshRequest): Observable<boolean> {
    return of(true);
  }

  save(callBack: Observer<boolean>) {
    callBack.next(true);
    callBack.complete();
  }

  abstract saveDashboardPanelState(): void;
  abstract clearDashboardPanelState(): void;

  protected openNavigationInOverlay(url: string, navigationName: string, overlayNavType: 'list' | 'form' | 'page', event: any): void {
    if (this.lmOverlayService.isOpening) {
      return;
    }
    this.lmOverlayService.isOpening = true;

    const info: LMDialogInfo = { message: Soho.Locale.translate('UnsavedChangesContinuingMsg')  };
    // Need to make sure the parent runs the unsaved changes, for the case that a parent list is dirty and
    // a navigation is clicked on the child
    this.lmOverlayService.routerOutletUnsavedChanges(info).subscribe((response) => {
      if (response){
        // need in a timeout for the case where it's a list on a page, unsaved changes is seen and
        // the user clicked to save. This causes the page to reload, so we need to be in the next tick
        // to not have an error
        this.zone.runOutsideAngular(() => {
          setTimeout(() => this._openNavigationInOverlay(url, navigationName, overlayNavType, event), 1);
        });
      } else {
        this.lmOverlayService.isOpening = false;
      }
    });
  }

  private _openNavigationInOverlay(url: string, navigationName: string, overlayNavType: 'list' | 'form' | 'page', event: LMFormOverlayClickedEvent) {
    const navigationUrl = this.lmUrlUtilService.updateUrlParams(url, { output: 'spec' });
    this.lmNavigationService.getActionNavigationUrl(navigationUrl).subscribe(response => {
      if (response.messageId && !response.redirectUrl) {
        // Common Inbox Integration - Direct route to open notification may return error messages.
        this.lmErrorUtilService.handleError(Soho.Locale.translate(response.messageId));
        this.lmOverlayService.isOpening = false;
        return;
      } else if (response.status && response.status === AppConstants.STATUS_SYSTEM_ERROR) {
        this.lmErrorUtilService.handleError(response.message, response.details, response.status, response.statusText);
        this.lmOverlayService.isOpening = false;
        return;
      }

      const redirectUrl = this.lmUrlUtilService.toRelativeUrl(response.redirectUrl, true, true);

      let relativeUrl = this.lmUrlUtilService.toRelativeUrl(redirectUrl);
      relativeUrl = this.lmUrlUtilService.encodePlus(relativeUrl);
      relativeUrl = this.lmUrlUtilService.encodeParens(relativeUrl);

      const isGenUrl = this.lmUrlUtilService.isGenUrlUsingHref(relativeUrl); // urlParams['csk.gen'] === 'true';
      const urlPath = this.lmUrlUtilService.getUrlPath(relativeUrl);
      const newResource = this.lmUrlUtilService.getResource(urlPath, isGenUrl);
      const newResourceId = newResource.generateBusObjectId(false);

      // worry about optimization for opening the same nav on a different record in the future
      this.lmOverlayService.open({
        open: true,
        url: relativeUrl,
        listOpenedId: this.panel.id,
        resourceId: newResourceId,
        isNavigation: true,
        navigationName: navigationName,
        replaceCurrentContent: this.lmOverlayService.hasOpenOverlay,
        navigationType: overlayNavType,
        insideOverlayNav: this.isFlyoutContent,
        event: event
      });
    });
  }
}
